// components/interview/InterviewManagement.js

import React, { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  GridLegacy as Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Divider,
  Stack,
  TextField,
  Slider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

import {
  Schedule as ScheduleIcon,
  VideoCall as VideoCallIcon,
  CheckCircle as CompletedIcon,
  Pending as PendingIcon,
  Person as CandidateIcon,
  Business as VendorIcon,
  Assessment as FeedbackIcon,
  Star as RatingIcon,
  AccessTime as TimeIcon,
  Today as TodayIcon,
  Warning as UrgentIcon,
  Launch as JoinIcon
} from '@mui/icons-material';

import { format, isToday, isTomorrow, parseISO } from 'date-fns';
import { useAuth } from '../../utils/AuthContext';
import { panelistAPI, vendorAPI ,adminAPI } from '../../services/api';

const InterviewManagement = ({ contentType = 'upcoming' }) => {
  const { user } = useAuth();

  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [upcomingInterviews, setUpcomingInterviews] = useState([]);
  const [completedInterviews, setCompletedInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);

  // ✅ FIXED: Complete feedback form state with all required fields
  const [feedback, setFeedback] = useState({
    overall_rating: 1, // ✅ Changed from 0 to 1 (required 1-5)
    technical_rating: 0,
    communication_rating: 0,
    problem_solving_rating: 0,
    cultural_fit_rating: 0,
    final_verdict: '',
    feedback_notes: '',
    strengths: '',
    areas_for_improvement: '',
    recommendations: ''
  });

  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  // ✅ NEW: Reschedule state
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedInterviewForReschedule, setSelectedInterviewForReschedule] = useState(null);
  const [rescheduleDateTime, setRescheduleDateTime] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [rescheduling, setRescheduling] = useState(false);

  // ✅ Role-based API loading with enhanced debugging
  const loadInterviews = useCallback(async (type = 'upcoming') => {
    try {
        setLoading(true);
        setError(null);

        console.log(`🔄 InterviewManagement: Loading ${type} interviews for user role:`, user?.role);
        console.log(`🔄 InterviewManagement: User object:`, user);

        let response;

        if (user?.role === 'admin') {
            console.log(`🔄 InterviewManagement: Using admin API for ${type} interviews`);
            if (type === 'upcoming') {
                response = await adminAPI.getSharedUpcomingInterviews();
                console.log(`✅ InterviewManagement: Admin API response:`, response.data);
                setUpcomingInterviews(response.data.interviews || []);
            } else {
                response = await adminAPI.getSharedCompletedInterviews();
                console.log(`✅ InterviewManagement: Admin API response:`, response.data);
                setCompletedInterviews(response.data.interviews || []);
            }
        } else if (user?.role === 'panelist') {
            console.log(`🔄 InterviewManagement: Using panelist API for ${type} interviews`);
            if (type === 'upcoming') {
                response = await panelistAPI.getSharedUpcomingInterviews();
                console.log(`✅ InterviewManagement: Panelist API response:`, response.data);
                setUpcomingInterviews(response.data.interviews || []);
            } else {
                response = await panelistAPI.getSharedCompletedInterviews();
                console.log(`✅ InterviewManagement: Panelist API response:`, response.data);
                setCompletedInterviews(response.data.interviews || []);
            }
        } else if (user?.role === 'vendor') {
            console.log(`🔄 InterviewManagement: Using vendor API for ${type} interviews`);
            if (type === 'upcoming') {
                response = await vendorAPI.getUpcomingInterviews();
                console.log(`✅ InterviewManagement: Vendor API response:`, response.data);
                setUpcomingInterviews(response.data.interviews || []);
            } else {
                response = await vendorAPI.getCompletedInterviews();
                console.log(`✅ InterviewManagement: Vendor API response:`, response.data);
                setCompletedInterviews(response.data.interviews || []);
            }
        } else {
            console.error(`❌ InterviewManagement: Unsupported user role:`, user?.role);
            setError(`User role '${user?.role}' not supported for interviews`);
            return;
        }

        console.log(`✅ InterviewManagement: Successfully loaded ${response.data.interviews?.length || 0} ${type} interviews for ${user?.role}`);
    } catch (error) {
        console.error(`❌ InterviewManagement: Error loading ${type} interviews:`, error);
        console.error(`❌ InterviewManagement: Error details:`, error.response?.data || error.message);
        setError(`Failed to load ${type} interviews: ${error.message}`);
    } finally {
        setLoading(false);
    }
}, [user]);

  useEffect(() => {
    const type = activeTab === 0 ? 'upcoming' : 'completed';
    loadInterviews(type);
  }, [activeTab, loadInterviews]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Get priority styling for interviews
  const getInterviewPriority = (interview) => {
    if (!interview.time_info) return 'normal';
    if (interview.time_info.is_urgent) return 'urgent';
    if (interview.time_info.is_today) return 'today';
    if (interview.time_info.status === 'this_week') return 'week';
    return 'normal';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'today': return 'warning';
      case 'week': return 'info';
      default: return 'default';
    }
  };

  // Format interview time
  const formatInterviewTime = (dateString) => {
    const date = parseISO(dateString);
    if (isToday(date)) {
      return `Today at ${format(date, 'HH:mm')}`;
    } else if (isTomorrow(date)) {
      return `Tomorrow at ${format(date, 'HH:mm')}`;
    } else {
      return format(date, 'MMM dd, yyyy HH:mm');
    }
  };

  // Get role-specific interview actions
  const getInterviewActions = (interview) => {
    const actions = [];

    // Join meeting action (all roles)
    if (interview.meeting_link) {
      actions.push({
        label: 'Join Meeting',
        icon: <VideoCallIcon />,
        color: 'primary',
        onClick: () => window.open(interview.meeting_link, '_blank')
      });
    }

    // Complete interview action (panelists only for upcoming interviews)
    if (user?.role === 'panelist' && (interview.status === 'scheduled' || interview.status === 'pending')) {
      actions.push({
        label: 'Complete Interview',
        icon: <CompletedIcon />,
        color: 'success',
        onClick: () => handleCompleteInterview(interview)
      });
    }

    // ✅ NEW: Reschedule interview action (panelists only for upcoming interviews)
    if (user?.role === 'panelist' && (interview.status === 'scheduled' || interview.status === 'pending')) {
      actions.push({
        label: 'Reschedule Interview',
        icon: <ScheduleIcon />,
        color: 'warning',
        onClick: () => handleOpenRescheduleDialog(interview)
      });
    }

    // View details action (all roles)
    actions.push({
      label: 'View Details',
      icon: <FeedbackIcon/>,
      color: 'info',
      onClick: () => handleViewDetails(interview)
    });

    return actions;
  };

  const handleCompleteInterview = (interview) => {
    setSelectedInterview(interview);
    // ✅ FIXED: Reset form with proper defaults
    setFeedback({
      overall_rating: 1, // ✅ Default to 1 instead of 0
      technical_rating: 0,
      communication_rating: 0,
      problem_solving_rating: 0,
      cultural_fit_rating: 0,
      final_verdict: '',
      feedback_notes: '',
      strengths: '',
      areas_for_improvement: '',
      recommendations: ''
    });
    setFeedbackDialogOpen(true);
  };

const handleViewDetails = async (interview) => {
    try {
        let response;
        if (user?.role === 'admin') {
            response = await adminAPI.getSharedInterviewDetails(interview.id);
        } else if (user?.role === 'panelist') {
            response = await panelistAPI.getSharedInterviewDetails(interview.id);
        } else if (user?.role === 'vendor') {
            response = await vendorAPI.getInterviewDetails(interview.id);
        }

        if (response) {
            setSelectedInterview(response.data.interview);
            setDetailsDialogOpen(true);
        }
    } catch (error) {
        console.error('Error loading interview details:', error);
    }
};

  // ✅ NEW: Reschedule functions
  const handleOpenRescheduleDialog = (interview) => {
    setSelectedInterviewForReschedule(interview);
    setRescheduleDateTime('');
    setRescheduleReason('');
    setRescheduleDialogOpen(true);
  };

  const handleRescheduleInterview = async () => {
    if (!rescheduleDateTime.trim()) {
      alert('Please select a new date and time');
      return;
    }

    if (!rescheduleReason.trim()) {
      alert('Please provide a reason for rescheduling');
      return;
    }

    try {
      setRescheduling(true);
      
      const rescheduleData = {
        new_datetime: rescheduleDateTime,
        reschedule_reason: rescheduleReason.trim()
      };

      await panelistAPI.rescheduleInterview(selectedInterviewForReschedule.id, rescheduleData);
      
      setRescheduleDialogOpen(false);
      setSelectedInterviewForReschedule(null);
      setRescheduleDateTime('');
      setRescheduleReason('');
      
      // Refresh interviews
      loadInterviews('upcoming');
      loadInterviews('completed');
      
      alert('Interview rescheduled successfully! Email notifications have been sent.');
    } catch (error) {
      console.error('❌ Failed to reschedule interview:', error);
      alert(`Failed to reschedule interview: ${error.response?.data?.error || error.message}`);
    } finally {
      setRescheduling(false);
    }
  };

  // ✅ FIXED: Enhanced submit feedback with proper validation
  const handleSubmitFeedback = async () => {
    // Enhanced validation
    console.log('🔍 Interview being completed:', selectedInterview);
    console.log('🔍 Interview ID:', selectedInterview?.id);
    if (!feedback.overall_rating || feedback.overall_rating < 1 || feedback.overall_rating > 5) {
      alert('Overall rating must be between 1 and 5');
      return;
    }

    if (!feedback.final_verdict) {
      alert('Please select a final decision');
      return;
    }

    if (!feedback.feedback_notes || feedback.feedback_notes.trim().length < 10) {
      alert('Please provide detailed feedback (at least 10 characters)');
      return;
    }

    try {
      setSubmittingFeedback(true);
      
      // ✅ FIXED: Ensure all numeric fields are properly formatted
      const formattedFeedback = {
        overall_rating: Number(feedback.overall_rating),
        technical_rating: Number(feedback.technical_rating) || 0,
        communication_rating: Number(feedback.communication_rating) || 0,
        problem_solving_rating: Number(feedback.problem_solving_rating) || 0,
        cultural_fit_rating: Number(feedback.cultural_fit_rating) || 0,
        final_verdict: feedback.final_verdict,
        feedback_notes: feedback.feedback_notes.trim(),
        strengths: (feedback.strengths || '').trim(),
        areas_for_improvement: (feedback.areas_for_improvement || '').trim(),
        recommendations: (feedback.recommendations || '').trim()
      };

      console.log('🔄 Submitting feedback:', formattedFeedback);

      await panelistAPI.completeInterview(selectedInterview.id, formattedFeedback);
      alert('Interview completed successfully!');
      setFeedbackDialogOpen(false);
      
      // Reset form
      setFeedback({
        overall_rating: 1,
        technical_rating: 0,
        communication_rating: 0,
        problem_solving_rating: 0,
        cultural_fit_rating: 0,
        final_verdict: '',
        feedback_notes: '',
        strengths: '',
        areas_for_improvement: '',
        recommendations: ''
      });
      
      // Refresh interviews
      loadInterviews('upcoming');
    } catch (error) {
      console.error('Failed to complete interview:', error);
      const errorMessage = error.response?.data?.error || 'Failed to complete interview';
      alert(errorMessage);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  // Render upcoming interviews
  const renderUpcomingInterviews = () => {
    if (upcomingInterviews.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <ScheduleIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No upcoming interviews
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All interviews are up to date!
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 180 }}><strong>Candidate</strong></TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 150 }}><strong>Job Title</strong></TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 180 }}><strong>Scheduled Time</strong></TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}><strong>Panelist</strong></TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 120 }}><strong>Vendor</strong></TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 100 }}><strong>Status</strong></TableCell>
              <TableCell align="center" sx={{ fontWeight: 'bold', minWidth: 280 }}><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {upcomingInterviews.map((interview) => {
              const priority = getInterviewPriority(interview);
              const priorityColor = getPriorityColor(priority);

              return (
                <TableRow key={interview.id} hover>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        {interview.candidate_name}
                      </Typography>
                      {priority === 'urgent' && (
                        <Chip
                          icon={<UrgentIcon />}
                          label="URGENT"
                          color="error"
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        Job Code: {interview.job?.job_code || 'N/A'}
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {interview.job_title}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Chip
                        icon={<TimeIcon />}
                        label={formatInterviewTime(interview.scheduled_at)}
                        color={priorityColor}
                        size="small"
                        sx={{ mb: 0.5 }}
                      />
                      {interview.time_info?.hours_until && (
                        <Typography variant="caption" color="warning.main">
                          {interview.time_info.hours_until < 1
                            ? `Starting in ${Math.round(interview.time_info.hours_until * 60)} minutes`
                            : `Starting in ${Math.round(interview.time_info.hours_until)} hours`
                          }
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {interview.panelist?.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">
                      {interview.vendor?.name}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={interview.status || 'scheduled'}
                      color={interview.status === 'scheduled' ? 'primary' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      {getInterviewActions(interview).map((action, index) => (
                        <Button
                          key={index}
                          variant={index === 0 ? "contained" : "outlined"}
                          color={action.color}
                          size="small"
                          startIcon={action.icon}
                          onClick={action.onClick}
                        >
                          {action.label}
                        </Button>
                      ))}
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Render completed interviews table
  const renderCompletedInterviews = () => {
    if (completedInterviews.length === 0) {
      return (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <CompletedIcon sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No completed interviews
            </Typography>
          </CardContent>
        </Card>
      );
    }

    return (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Candidate</TableCell>
              <TableCell>Job Title</TableCell>
              <TableCell>Completed Date</TableCell>
              <TableCell>Overall Rating</TableCell>
              <TableCell>Final Verdict</TableCell>
              <TableCell>Panelist</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {completedInterviews.map((interview) => (
              <TableRow key={interview.id}>
                <TableCell>
                  <Box>
                    <Typography variant="subtitle2">{interview.candidate_name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {interview.candidate_email}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{interview.job_title}</TableCell>
                <TableCell>
                  {interview.completed_at ?
                    format(parseISO(interview.completed_at), 'MMM dd, yyyy HH:mm') :
                    'Not set'
                  }
                </TableCell>
                <TableCell>
                  {interview.ratings?.overall ? (
                    <Chip label={`${interview.ratings.overall}/5`} color="primary" size="small" />
                  ) : (
                    <Chip label="Not rated" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={interview.final_verdict} 
                    color={interview.final_verdict === 'hire' ? 'success' : interview.final_verdict === 'reject' ? 'error' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>{interview.panelist_name}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleViewDetails(interview)}
                    color="primary"
                    size="small"
                  >
                    <FeedbackIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading interviews...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab
          icon={<ScheduleIcon />}
          label="Upcoming Interviews"
          iconPosition="start"
        />
        <Tab
          icon={<CompletedIcon />}
          label="Completed Interviews"
          iconPosition="start"
        />
      </Tabs>

      {activeTab === 0 && renderUpcomingInterviews()}
      {activeTab === 1 && renderCompletedInterviews()}

      {/* 🎨 BEAUTIFUL & PROFESSIONAL: Complete Interview Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => setFeedbackDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          elevation: 12,
          sx: {
            borderRadius: 3,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            py: 3,
            textAlign: 'center'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            🎯 Complete Interview Assessment
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            {selectedInterview?.candidate_name} - {selectedInterview?.job_title}
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* ===== INTERVIEW CONTEXT SECTION ===== */}
          <Box sx={{ p: 3, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600 }}>
              📋 Interview Context
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Candidate: <Box component="span" sx={{ color: 'text.primary' }}>{selectedInterview?.candidate_name}</Box>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
                  Job Position: <Box component="span" sx={{ color: 'text.primary' }}>{selectedInterview?.job_title}</Box>
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Interview Date: <Box component="span" sx={{ color: 'text.primary' }}>
                    {selectedInterview?.scheduled_at ? format(parseISO(selectedInterview.scheduled_at), 'PPP p') : 'Not scheduled'}
                  </Box>
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, mt: 1 }}>
                  Panelist: <Box component="span" sx={{ color: 'text.primary' }}>{user?.username}</Box>
                </Typography>
              </Grid>
            </Grid>
          </Box>

          {/* ===== ASSESSMENT FORM ===== */}
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 600, mb: 3 }}>
              📊 Comprehensive Assessment
            </Typography>

            {/* ===== PRIMARY DECISIONS ===== */}
            <Card sx={{ mb: 4, border: 2, borderColor: 'primary.light', bgcolor: 'primary.50' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.dark', fontWeight: 600, mb: 3 }}>
                  🎯 Primary Assessment
                </Typography>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                        Overall Rating <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                      </Typography>
                      <Slider
                        value={feedback.overall_rating}
                        onChange={(event, newValue) => setFeedback({...feedback, overall_rating: newValue || 1})}
                        min={1}
                        max={5}
                        step={1}
                        marks={[
                          { value: 1, label: '1' },
                          { value: 2, label: '2' },
                          { value: 3, label: '3' },
                          { value: 4, label: '4' },
                          { value: 5, label: '5' }
                        ]}
                        valueLabelDisplay="auto"
                        sx={{
                          color: 'primary.main',
                          '& .MuiSlider-markLabel': { fontSize: '0.875rem', fontWeight: 600 }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Required: Rate the candidate's overall performance (1-5)
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl required>
                      <InputLabel sx={{ fontWeight: 600 }}>Final Recommendation</InputLabel>
                      <Select
                        value={feedback.final_verdict}
                        onChange={(e) => setFeedback({...feedback, final_verdict: e.target.value})}
                        label="Final Recommendation"
                        sx={{
                          minWidth: '500px',
                          width: '100%',
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2
                          },
                          '& .MuiSelect-select': {
                            whiteSpace: 'normal',
                            textOverflow: 'unset'
                          }
                        }}
                      >
                        <MenuItem value="hire" sx={{ fontWeight: 500 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: 'success.main' }}>🎉</Box>
                            Hire - Strong candidate, recommend proceeding
                          </Box>
                        </MenuItem>
                        <MenuItem value="maybe" sx={{ fontWeight: 500 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: 'warning.main' }}>🤔</Box>
                            Maybe - Needs more evaluation
                          </Box>
                        </MenuItem>
                        <MenuItem value="needs_another_round" sx={{ fontWeight: 500 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: 'info.main' }}>⚠️</Box>
                            Another Round - Additional assessment needed
                          </Box>
                        </MenuItem>
                        <MenuItem value="reject" sx={{ fontWeight: 500 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ color: 'error.main' }}>❌</Box>
                            Reject - Not suitable for this position
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ===== DETAILED COMPETENCY ASSESSMENT ===== */}
            <Card sx={{ mb: 4, border: 1, borderColor: 'success.light' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'success.main', fontWeight: 600, mb: 3 }}>
                  🏆 Competency Assessment
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Rate the candidate's performance in key areas (0 = Not Assessed)
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 2, color: 'success.dark' }}>
                        💡 Technical Skills
                      </Typography>
                      <Slider
                        value={feedback.technical_rating}
                        onChange={(event, newValue) => setFeedback({...feedback, technical_rating: newValue || 0})}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{
                          color: 'success.main',
                          '& .MuiSlider-markLabel': { fontSize: '0.75rem' }
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 2, color: 'warning.dark' }}>
                        🗣️ Communication Skills
                      </Typography>
                      <Slider
                        value={feedback.communication_rating}
                        onChange={(event, newValue) => setFeedback({...feedback, communication_rating: newValue || 0})}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{
                          color: 'warning.main',
                          '& .MuiSlider-markLabel': { fontSize: '0.75rem' }
                        }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 2, color: 'info.dark' }}>
                        🧠 Problem Solving
                      </Typography>
                      <Slider
                        value={feedback.problem_solving_rating}
                        onChange={(event, newValue) => setFeedback({...feedback, problem_solving_rating: newValue || 0})}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{
                          color: 'info.main',
                          '& .MuiSlider-markLabel': { fontSize: '0.75rem' }
                        }}
                      />
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500, mb: 2, color: 'secondary.dark' }}>
                        👥 Cultural Fit
                      </Typography>
                      <Slider
                        value={feedback.cultural_fit_rating}
                        onChange={(event, newValue) => setFeedback({...feedback, cultural_fit_rating: newValue || 0})}
                        min={0}
                        max={5}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{
                          color: 'secondary.main',
                          '& .MuiSlider-markLabel': { fontSize: '0.75rem' }
                        }}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* ===== QUALITATIVE FEEDBACK ===== */}
            <Card sx={{ border: 1, borderColor: 'grey.300' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600, mb: 3 }}>
                  📝 Detailed Feedback
                </Typography>

                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="📋 Comprehensive Assessment"
                      value={feedback.feedback_notes}
                      onChange={(e) => setFeedback({...feedback, feedback_notes: e.target.value})}
                      placeholder="Please provide detailed feedback covering the candidate's technical skills, communication abilities, problem-solving approach, cultural fit, and your overall assessment..."
                      required
                      helperText="This feedback will be shared with the hiring team and stored for future reference"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="💪 Key Strengths"
                      value={feedback.strengths}
                      onChange={(e) => setFeedback({...feedback, strengths: e.target.value})}
                      placeholder="What are the candidate's main strengths and positive qualities?"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="🎯 Areas for Improvement"
                      value={feedback.areas_for_improvement}
                      onChange={(e) => setFeedback({...feedback, areas_for_improvement: e.target.value})}
                      placeholder="What areas could the candidate improve on?"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="🚀 Recommendations & Next Steps"
                      value={feedback.recommendations}
                      onChange={(e) => setFeedback({...feedback, recommendations: e.target.value})}
                      placeholder="Any specific recommendations for next steps, additional training, or further evaluation?"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            bgcolor: 'grey.50',
            borderTop: 1,
            borderColor: 'divider',
            gap: 2
          }}
        >
          <Button
            onClick={() => setFeedbackDialogOpen(false)}
            size="large"
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 4,
              fontWeight: 600
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitFeedback}
            variant="contained"
            disabled={submittingFeedback || !feedback.overall_rating || !feedback.final_verdict}
            size="large"
            sx={{
              borderRadius: 2,
              px: 4,
              fontWeight: 600,
              minWidth: 200
            }}
          >
            {submittingFeedback ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} color="inherit" />
                Submitting...
              </Box>
            ) : (
              '✅ Complete Interview'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Interview Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Interview Details
        </DialogTitle>
        <DialogContent>
          {selectedInterview && (
            <Box>
              <Typography variant="body1">
                <strong>Candidate:</strong> {selectedInterview.candidate_name}
              </Typography>
              <Typography variant="body1">
                <strong>Job:</strong> {selectedInterview.job_title}
              </Typography>
              <Typography variant="body1">
                <strong>Scheduled:</strong> {formatInterviewTime(selectedInterview.scheduled_at)}
              </Typography>
              <Typography variant="body1">
                <strong>Status:</strong> {selectedInterview.status}
              </Typography>
              
              {selectedInterview.meeting_link && (
                <Button
                  onClick={() => window.open(selectedInterview.meeting_link, '_blank')}
                  variant="contained"
                  sx={{ mt: 2 }}
                  startIcon={<VideoCallIcon />}
                >
                  Join Meeting
                </Button>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ✅ NEW: Reschedule Interview Dialog */}
      <Dialog
        open={rescheduleDialogOpen}
        onClose={() => setRescheduleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleIcon color="warning" />
            Reschedule Interview
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedInterviewForReschedule && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="body1" gutterBottom>
                <strong>Candidate:</strong> {selectedInterviewForReschedule.candidate_name}
              </Typography>
              <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
                <strong>Current Schedule:</strong> {formatInterviewTime(selectedInterviewForReschedule.scheduled_at)}
              </Typography>

              <TextField
                label="New Date & Time"
                type="datetime-local"
                value={rescheduleDateTime}
                onChange={(e) => setRescheduleDateTime(e.target.value)}
                fullWidth
                margin="normal"
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  min: new Date().toISOString().slice(0, 16),
                }}
              />

              <TextField
                label="Reason for Rescheduling"
                multiline
                rows={3}
                value={rescheduleReason}
                onChange={(e) => setRescheduleReason(e.target.value)}
                fullWidth
                margin="normal"
                placeholder="Please provide a reason for rescheduling this interview..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRescheduleDialogOpen(false)}
            disabled={rescheduling}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRescheduleInterview}
            variant="contained"
            color="warning"
            disabled={rescheduling || !rescheduleDateTime || !rescheduleReason.trim()}
            sx={{ minWidth: 120 }}
          >
            {rescheduling ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Rescheduling...
              </Box>
            ) : (
              'Reschedule'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InterviewManagement;
